/* eslint-disable max-classes-per-file */
import React, {
    ReactElement, useContext, useEffect, useState,
} from "react";
import useKeyboardShortcuts, {
    KeyboardContextState, ScopedClientApi,
    useKeyboardState,
} from "../../contexts/KeyboardShortcutContext";
import useRouting, { Paths, RoutingProps } from "../../contexts/Routing";
import {
    ChatSource, ChatStatus, DashboardTicketState, ListAgentChatQuery, User,
} from "../../__generated__/api";

export interface ColumnGroup {
    title: string;
    id: string;
    fullId: string;
    subtitle?: string;
    imageUrl?: string;
    hideImage?: boolean;
    items: Ticket[];
    isCentered?: boolean;
}
export interface ColumnData {
    id: string;
    text: string;
    description?: string;
    state?: DashboardTicketState;
    groups: ColumnGroup[];
    statuses: Set<ChatStatus>;
    items: Ticket[];
}

type TicketFieldOverrides = {
    creator: Omit<User, "__typename" | "userGroups" | "role">;
    source?: Omit<ChatSource, "__typename"> | null | undefined;
};
type ApiTicket = Exclude<ListAgentChatQuery["chats"], undefined | null>["chats"][0];
export type Ticket = Omit<ApiTicket, keyof TicketFieldOverrides> & TicketFieldOverrides;

export enum SelectionType {
    NONE = "NONE",
    SELECTED = "SELECTED",
    NOTIFICATION = "NOTIFICATION",
    HIDDEN = "HIDDEN"
}

enum Direction {
    START,
    END
}

export enum AssignmentType {
    NONE = "NONE",
    CURRENT_USER = "CURRENT_USER",
    OTHER_USER = "OTHER_USER"
}

export enum SelectionFocus {
    FOCUS = "FOCUS",
    NONE = "NONE"
}

interface ChatState {
    setSelectionType: (type: SelectionType) => void;
    update: () => void;
    selectionType: SelectionType;
    reference: React.RefObject<HTMLAnchorElement>;
}

interface ChatStatusState {
    setStatus?: (type: ChatStatus) => void;
    status: ChatStatus;
    modified: Date;

    setAssignment?: (assignment: AssignmentType) => void;
    assignment: AssignmentType;
}

interface RegisterCardState {
    selection: SelectionType;
    version: number;
    isUnread: boolean;
    ref: React.RefObject<HTMLAnchorElement>;
}

interface ColumnPillState {
    refreshCount: () => void;
    count: number;
}

export enum MoveDirection {
    HORIZONTAL,
    VERTICAL
}

export class SelectionState {
    private direction = MoveDirection.HORIZONTAL;

    private selection: Set<string> = new Set();

    private stateOwners: Record<string, ChatState> = Object.create(null);

    private chatStatuses: Record<string, ChatStatusState> = Object.create(null);

    private selectedColumn = 0;

    private selectionStart?: number;

    private selectionEnd?: number;

    private selectedIndex?: number;

    private currentlyOpened?: string;

    private lastReadDates: Record<string, number> = Object.create(null);

    private columns?: ColumnData[];

    private columnPillContainers: Record<number, ColumnPillState> = Object.create(null);

    private itemMap: Record<string, {
        row: number;
        column: number;
        ticket: Ticket;
        state?: DashboardTicketState;
        group?: ColumnGroup;

    }> = Object.create(null);

    private groupsState: Record<string, {
        id: string;
        isMinimized: boolean;
        setIsMinimized: (handler: ((value: boolean) => boolean)) => void;
        group?: ColumnGroup;
    }> = Object.create(null);

    private openedHandler?: (id: string) => void;

    private deleteHandler?: (tickets: Ticket[]) => void;

    private transferHandler?: (tickets: Ticket[]) => void;

    private ignoreHandler?: (tickets: Ticket[]) => void;

    private endChatHandler?: (tickets: Ticket[]) => void;

    private assignHandler?: (tickets: Ticket[]) => void;

    private unassignHandler?: (tickets: Ticket[]) => void;

    private snoozeHandler?: (tickets: Ticket[]) => void;

    private holdHandler?: (tickets: Ticket[]) => void;

    private takeOverHandler?: (tickets: Ticket[]) => void;

    private reopenHandler?: (tickets: Ticket[]) => void;

    private assignSelfHandler?: (tickets: Ticket[]) => void;

    private addParticipantsHandler?: (tickets: Ticket[]) => void;

    private participanteHandler?: (tickets: Ticket[]) => void;

    private leaveHandler?: (tickets: Ticket[]) => void;

    private markReadHandler?: (tickets: Ticket[]) => void;

    private markUnreadHandler?: (tickets: Ticket[]) => void;

    private setChatStateHandler?: (id: string[], state: DashboardTicketState, group: ColumnGroup) => void;

    private routing?: RoutingProps;

    private keyboardState?: KeyboardContextState;

    constructor(routing?: RoutingProps, keyboardState?: KeyboardContextState) {
        this.routing = routing;
        this.keyboardState = keyboardState;
    }

    public registerChatStatusAssignment(
        id: string | undefined,
        defaultChat: ChatStatus | undefined,
        defaultAssignment = AssignmentType.NONE,
    ): [ChatStatus, AssignmentType] {
        const currentValue = defaultChat ?? ChatStatus.ChatRequested;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [status, stateSetter] = useState<ChatStatus>(currentValue);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [assignment, assignmentSetter] = useState<AssignmentType>(defaultAssignment);
        if (!id) {
            return [status, assignment];
        }
        let current = this.chatStatuses[id];
        if (!this.chatStatuses[id]) {
            current = {
                status,
                modified: new Date(),
                assignment,
            };
            this.chatStatuses[id] = current;
        }
        current.setStatus = value => {
            current.status = value;
            current.modified = new Date();
            stateSetter(value);
        };
        current.setAssignment = value => {
            current.assignment = value ?? current.assignment;
            assignmentSetter(value);
        };

        return [
            status,
            assignment,
        ];
    }

    public registerCard(id: string): RegisterCardState {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [selection, stateSetter] = useState(SelectionType.NONE);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [version, setVersion] = useState(100);

        let current = this.stateOwners[id];
        if (!current) {
            current = {
                reference: React.createRef(),
                selectionType: selection,
            } as ChatState;
            this.stateOwners[id] = current;
        }
        current.setSelectionType = type => {
            if (current.selectionType === SelectionType.SELECTED
                && type !== SelectionType.NONE
                && type !== SelectionType.HIDDEN) {
                return;
            }
            current.selectionType = type;
            stateSetter(type);
        };
        current.update = () => {
            setVersion(curr => curr + 1);
        };
        if (selection === SelectionType.SELECTED || selection === SelectionType.HIDDEN) {
            return {
                selection,
                isUnread: this.isUnread(id),
                ref: current.reference,
                version,
            };
        }

        return {
            selection,
            isUnread: this.isUnread(id),
            ref: current.reference,
            version,
        };
    }

    public registerColumnPill(index: number): number {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [currentValue, stateSetter] = useState(() => this.getColumnUnreadCount(index));
        let current = this.columnPillContainers[index];
        if (!current) {
            current = {
                refreshCount: () => {
                    current.count = this.getColumnUnreadCount(index);
                    stateSetter(current.count);
                },
                count: 0,
            };
            this.columnPillContainers[index] = current;
        } else {
            current.refreshCount = () => {
                current.count = this.getColumnUnreadCount(index);
                stateSetter(current.count);
            };
        }

        return currentValue;
    }

    public registerGroupMinimize(group: ColumnGroup): [boolean, (handler: ((value: boolean) => boolean)) => void] {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [isMinimized, setIsMinimized] = useState(false);
        const id = group.fullId ?? group.id;
        let current = this.groupsState[id];
        if (!current) {
            current = {
                id,
                isMinimized,
                setIsMinimized: callback => {
                    const newValue = callback(current.isMinimized);
                    current.isMinimized = newValue;
                    setIsMinimized(newValue);
                },
                group,
            };
            this.groupsState[id] = current;
        } else {
            current.isMinimized = isMinimized;
            current.setIsMinimized = callback => {
                const newValue = callback(current.isMinimized);
                current.isMinimized = newValue;
                setIsMinimized(newValue);
            };
        }

        return [isMinimized, setIsMinimized];
    }

    public minimizeAll(): void {
        const all = Object.values(this.groupsState);
        const isAllMin = all.every(row => row.isMinimized);
        all.forEach(row => row.setIsMinimized(() => !isAllMin));
    }

    public getChat(id: string): Ticket | undefined {
        const ticket = this.itemMap[id]?.ticket;
        return this.chatStatuses[id] && ticket ? {
            ...ticket,
            status: this.chatStatuses[id].status,
        } : ticket;
    }

    public setChat(id: string, ticket: Ticket): void {
        const current = this.itemMap[id] ?? {
            column: -1,
            row: -1,
        };
        current.ticket = ticket;
        this.itemMap[id] = current;
        this.updateCard(id);
    }

    public getChats(ids: string[]): Ticket[] {
        return ids.map(id => this.getChat(id)).filterNull();
    }

    public getChatState(id: string): DashboardTicketState | undefined {
        return this.itemMap[id]?.state;
    }

    public getColumn(id: string): ColumnData | undefined {
        const columnIndex = this.itemMap[id]?.column;
        return columnIndex != null ? this.columns?.[columnIndex] : undefined;
    }

    public getChatStatus(id: string, ticket?: Ticket): ChatStatus | undefined {
        const state = this.chatStatuses[id];

        const ticketModified = typeof ticket?.modifiedDate === "string" ? new Date(ticket?.modifiedDate) : ticket?.modifiedDate;
        return (!state || state.modified.getTime() < (ticketModified?.getTime() ?? 0))
            ? ticket?.status
            : state?.status;
    }

    public getSelectionType(id: string): SelectionType | undefined {
        return this.stateOwners[id]?.selectionType;
    }

    public getGroup(id: string): ColumnGroup | undefined {
        return this.itemMap[id]?.group;
    }

    public registerSetChatStateHandler(
        handler: ((ids: string[], state: DashboardTicketState, group: ColumnGroup) => void),
    ): void {
        this.setChatStateHandler = handler;
    }

    public setChatState(ids: string[], state: DashboardTicketState, group: ColumnGroup): void {
        ids.forEach(id => {
            const row = this.itemMap[id];
            if (!row) {
                throw new Error(`Setting ticket state for invalid id. ID: ${id}`);
            }
            row.state = state;
            row.group = group;
        });

        this.setChatStateHandler?.(ids, state, group);
    }

    public unselect(id: string): void {
        this.deleteSelection(id);
    }

    public isSelected(id: string): boolean {
        return this.selection.has(id);
    }

    public isUnread(id: string): boolean {
        const item = this.itemMap[id];
        if (!item) {
            return false;
        }
        const { ticket } = this.itemMap[id];
        if (!ticket) {
            return false;
        }
        const messageDate = new Date(ticket.lastUserMessageDate ?? Date.now());
        const lastReadDate = new Date(this.lastReadDates[id] ?? ticket.currentUserLastReadDate ?? 0);

        return messageDate.getTime() > lastReadDate.getTime();
    }

    public getColumnUnreadCount(columnIndex: number): number {
        const column = this.columns?.[columnIndex];
        return column?.items.count(item => this.isUnread(item.id)) ?? 0;
    }

    public getGroupUnreadCount(group: ColumnGroup): number {
        return group?.items.count(item => this.isUnread(item.id)) ?? 0;
    }

    public scrollToFirstUnread(columnIndex: number): void {
        const column = this.columns?.[columnIndex];
        const foundItem = column?.items.find(item => this.isUnread(item.id));
        if (foundItem) {
            this.scrollToItem(foundItem.id);
        }
    }

    public markAllRead(columnIndex: number): void {
        const column = this.columns?.[columnIndex];
        const foundItems = column?.items.filter(item => this.isUnread(item.id));
        const ids = foundItems?.toIdList("id");
        if (ids) {
            this.markRead(...ids);
        }
    }

    public registerMarkReadHandler(handler: ((tickets: Ticket[]) => void)): void {
        this.markReadHandler = handler;
    }

    public markRead(...ids: string[]): void {
        const columns = ids.map(id => {
            const currentItem = this.itemMap[id];
            const lastUserDate = currentItem?.ticket?.lastUserMessageDate;
            this.lastReadDates[id] = Math.max((lastUserDate ? new Date(lastUserDate).getTime() : 0), Date.now());
            this.stateOwners[id]?.setSelectionType(SelectionType.NONE);
            return this.itemMap[id]?.column;
        });

        columns.filterNull().dedupe().forEach(col => {
            this.columnPillContainers[col]?.refreshCount();
        });

        this.markReadHandler?.(ids.map(id => this.itemMap[id]?.ticket).filterNull());
    }

    public registerMarkUnreadHandler(handler: ((tickets: Ticket[]) => void)): void {
        this.markUnreadHandler = handler;
    }

    private markIdsUnread(ids: string[], selectionType?: SelectionType): void {
        const columns = ids.map(id => {
            this.lastReadDates[id] = 0;
            if (selectionType) {
                this.stateOwners[id]?.setSelectionType(selectionType);
            } else {
                this.markUnreadHandler?.([this.itemMap[id]?.ticket].filterNull());
            }
            return this.itemMap[id]?.column;
        });
        columns.filterNull().dedupe().forEach(col => this.columnPillContainers[col]?.refreshCount());
    }

    public markCacheUnread(...ids: string[]): void {
        this.markIdsUnread(ids, SelectionType.NOTIFICATION);
    }

    public markUnread(...ids: string[]): void {
        this.markIdsUnread(ids);
    }

    public setMoveDirection(direction: MoveDirection) {
        this.direction = direction;
    }

    public findColumn(handler: ((column: ColumnData) => boolean)): ColumnData | undefined {
        return this.columns?.find(handler);
    }

    public setSearchResults(tickets: Ticket[]): void {
        tickets.forEach(ticket => {
            this.itemMap[ticket.id] = {
                column: -1,
                row: -1,
                ticket,
            };
        });
    }

    public addItem(ticket: Ticket): void {
        this.itemMap[ticket.id] = {
            row: -1,
            column: -1,
            ticket,
            state: {

            } as DashboardTicketState,
        };
        this.setChatStatus(
            ticket.id,
            ticket.status,
            ticket.isAssignedToCurrentUser ? AssignmentType.CURRENT_USER : AssignmentType.OTHER_USER,
        );
    }

    public setColumns(columns: ColumnData[]): void {
        if (!columns) {
            return;
        }
        this.columns = columns;
        const ignoredItems = Object.values(this.itemMap).filter(row => row.column === -1);
        this.itemMap = Object.create(null);
        ignoredItems.forEach(row => {
            this.itemMap[row.ticket.id] = row;
        });
        this.columns.forEach((items, column) => {
            const groups = items.groups
                .flatMap(group => group.items.map(item => ({ itemId: item.id, group })))
                .toIdMap("itemId", "group");
            items.items.forEach((item, row) => {
                this.itemMap[item.id] = {
                    column,
                    row,
                    ticket: item,
                    state: items.state,
                    group: groups[item.id],
                };
                this.setChatStatus(
                    item.id,
                    item.status,
                    item.isAssignedToCurrentUser ? AssignmentType.CURRENT_USER : AssignmentType.OTHER_USER,
                );
            });

            this.columnPillContainers[column]?.refreshCount();
        });
    }

    private statusUpdateHandlers: ((id: string, status: ChatStatus) => void)[] = [];

    public useUpdate(update: ((id: string, status: ChatStatus) => void)): void {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            this.statusUpdateHandlers.push(update);
            return () => {
                this.statusUpdateHandlers = this.statusUpdateHandlers.filter(item => item !== update);
            };
        }, []);
    }

    public setChatStatus(id: string, status: ChatStatus, assignment?: AssignmentType) {
        this.chatStatuses[id] = this.chatStatuses[id] ?? Object.create(null);
        const current = this.chatStatuses[id];
        if (current.setStatus) {
            const isChanged = status !== current.status;
            current.setStatus(status);
            if (assignment && current.setAssignment) {
                current.setAssignment(assignment);
            }
            if (isChanged) {
                this.statusUpdateHandlers.forEach(row => row(id, status));
            }
        } else {
            current.status = status;
            current.modified = new Date();
            current.assignment = assignment ?? current.assignment;
        }
    }

    public setChatStatuses(ids: string[], status: ChatStatus, assignment?: AssignmentType) {
        ids.forEach(id => this.setChatStatus(id, status, assignment));
    }

    public getSelection(): Ticket[] {
        const rows = Array.from(this.selection);
        return rows.map(id => {
            const ticket = this.itemMap[id]?.ticket;
            return ticket ? {
                ...ticket,
                status: this.chatStatuses[id].status ?? ticket.status,
            } : undefined;
        }).filterNull();
    }

    public registerDeleteHandler(handler: ((tickets: Ticket[]) => void)): void {
        this.deleteHandler = handler;
    }

    public delete(...ids: string[]): void {
        this.deleteHandler?.(ids.map(id => this.itemMap[id]?.ticket).filterNull());
        ids.forEach(id => {
            delete this.itemMap[id];
        });
    }

    public deleteSelected(): void {
        this.delete(...Array.from(this.selection));
    }

    public registerTransferHandler(handler: ((tickets: Ticket[]) => void)): void {
        this.transferHandler = handler;
    }

    public registerAddParticipantsHandler(handler: ((tickets: Ticket[]) => void)): void {
        this.addParticipantsHandler = handler;
    }

    public addParticipants(...ids: string[]): void {
        this.addParticipantsHandler?.(ids.map(id => this.itemMap[id]?.ticket).filterNull());
    }

    public registerParticipateHandler(handler: ((tickets: Ticket[]) => void)): void {
        this.participanteHandler = handler;
    }

    public participate(...ids: string[]): void {
        this.participanteHandler?.(ids.map(id => this.itemMap[id]?.ticket).filterNull());
    }

    public registerLeaveHandler(handler: ((tickets: Ticket[]) => void)): void {
        this.leaveHandler = handler;
    }

    public leave(...ids: string[]): void {
        this.leaveHandler?.(ids.map(id => this.itemMap[id]?.ticket).filterNull());
    }

    public tranfer(...ids: string[]): void {
        this.transferHandler?.(ids.map(id => this.itemMap[id]?.ticket).filterNull());
    }

    public tranferSelected(): void {
        this.tranfer(...Array.from(this.selection));
    }

    public registerAssignHandler(handler: ((tickets: Ticket[]) => void)): void {
        this.assignHandler = handler;
    }

    public assign(...ids: string[]): void {
        this.assignHandler?.(ids.map(id => this.itemMap[id]?.ticket).filterNull());
    }

    public registerTakeOverHandler(handler: ((tickets: Ticket[]) => void)): void {
        this.takeOverHandler = handler;
    }

    public takeOver(...ids: string[]): void {
        this.takeOverHandler?.(ids.map(id => this.itemMap[id]?.ticket).filterNull());
    }

    public registerReopenHandler(handler: ((tickets: Ticket[]) => void)): void {
        this.reopenHandler = handler;
    }

    public reopen(...ids: string[]): void {
        this.reopenHandler?.(ids.map(id => this.itemMap[id]?.ticket).filterNull());
    }

    public registerAssignSelfHandler(handler: ((tickets: Ticket[]) => void)): void {
        this.assignSelfHandler = handler;
    }

    public assignSelf(...ids: string[]): void {
        this.assignSelfHandler?.(ids.map(id => this.itemMap[id]?.ticket).filterNull());
    }

    public assignSelected(): void {
        this.assign(...Array.from(this.selection));
    }

    public registerUnassignHandler(handler: ((tickets: Ticket[]) => void)): void {
        this.unassignHandler = handler;
    }

    public unassign(...ids: string[]): void {
        this.unassignHandler?.(ids.map(id => this.itemMap[id]?.ticket).filterNull());
    }

    public registerSnooze(handler: ((tickets: Ticket[]) => void)): void {
        this.snoozeHandler = handler;
    }

    public snooze(...ids: string[]): void {
        this.snoozeHandler?.(ids.map(id => this.itemMap[id]?.ticket).filterNull());
    }

    public registerHold(handler: ((tickets: Ticket[]) => void)): void {
        this.holdHandler = handler;
    }

    public hold(...ids: string[]): void {
        this.holdHandler?.(ids.map(id => this.itemMap[id]?.ticket).filterNull());
    }

    public registerIgnoreHandler(handler: ((tickets: Ticket[]) => void)): void {
        this.ignoreHandler = handler;
    }

    public ignore(...ids: string[]): void {
        this.ignoreHandler?.(ids.map(id => this.itemMap[id]?.ticket).filterNull());
    }

    public ignoreSelected(): void {
        this.ignore(...Array.from(this.selection));
    }

    public registerEndChatHandler(handler: ((tickets: Ticket[]) => void)): void {
        this.endChatHandler = handler;
    }

    public endChat(...ids: string[]): void {
        this.endChatHandler?.(ids.map(id => this.itemMap[id]?.ticket).filterNull());
    }

    public endChatSelected(): void {
        this.endChat(...Array.from(this.selection));
    }

    public hasChat(id: string): boolean {
        return this.itemMap[id] != null;
    }

    public get selected(): Ticket | undefined {
        const index = this.selectedIndex;
        const ticket = index != null ? this.useColumn(col => col.items[index]) : undefined;
        return ticket ? this.getChat(ticket.id) : undefined;
    }

    public unselectAll(...ids: string[]): void {
        delete this.selectionStart;
        delete this.selectionEnd;
        if (this.selectedIndex != null) {
            const { selected } = this;
            if (ids.some(item => item === selected?.id)) {
                delete this.selectedIndex;
            }
        }
        (ids.isNotEmpty ? ids : Array.from(this.selection)).forEach(id => {
            this.deleteSelection(id);
        });
    }

    public clearSelection(): void {
        delete this.selectionEnd;
        delete this.selectionStart;

        this.useColumn(col => {
            const item = this.selectedIndex ? col.items[this.selectedIndex] : undefined;
            Array.from(this.selection).forEach(id => {
                if (item?.id !== id) {
                    this.deleteSelection(id);
                }
            });
            if (item) {
                this.addSelection(item.id, SelectionFocus.NONE);
            }
        });
    }

    private useColumn<T = void>(handler: ((column: ColumnData) => T)): T | undefined {
        if (!this.columns) {
            return undefined;
        }
        const column = this.columns[this.selectedColumn];
        return column ? handler(column) : undefined;
    }

    private static scrollTo(current: HTMLElement) {
        const parent = current.closest(".column-group") as HTMLElement;
        if (!parent) {
            return;
        }
        const group = parent.closest(".group-container") as HTMLElement;
        if (group) {
            if (group.offsetTop < window.scrollY) {
                const column = group.closest(".column-items") as HTMLElement;
                if (column && column.querySelector(".group-container") === group) {
                    window.scrollTo({
                        top: 0,
                    });
                } else {
                    group.scrollIntoView({
                        block: "start",
                    });
                }
            } else if (group.offsetTop + group.clientHeight > window.scrollY + window.innerHeight) {
                group.scrollIntoView({
                    block: "end",
                });
            }
        }
        const parentScrollTop = parent?.scrollTop ?? 0;
        if ((parent.offsetHeight + parentScrollTop + parent.offsetTop < (current.offsetTop + current.offsetHeight)
            || (parentScrollTop + parent.offsetTop > current.offsetTop))) {
            parent?.scrollTo({
                behavior: "smooth",
                top: current.offsetTop - parent.offsetTop,
            });
        }
    }

    public ensureSelected(id: string): void {
        if (!this.selection.has(id)) {
            this.toggleSelection(id);
        }
    }

    public registerOpenedHandler(handler: ((id: string) => void)): void {
        this.openedHandler = handler;
    }

    public setOpened(id: string): void {
        this.currentlyOpened = id;
        this.clearSelection();
        this.ensureSelected(id);
        this.markRead(id);
        this.openedHandler?.(id);
    }

    public toggleSelection(id: string): void {
        const selected = this.itemMap[id];

        const isSelected = this.selection.has(id);
        if (isSelected) {
            this.deleteSelection(id);
        } else {
            this.addSelection(id, SelectionFocus.NONE);
        }

        if (selected) {
            const { row, column } = selected;
            if (!isSelected) {
                this.selectedColumn = column;
                this.selectedIndex = row;
            }
        }

        const card = this.stateOwners[id];
        if (card) {
            card.setSelectionType(isSelected ? SelectionType.NONE : SelectionType.SELECTED);
        }

        delete this.selectionStart;
        delete this.selectionEnd;
    }

    public updateCard(id: string) {
        const card = this.stateOwners[id];
        card?.update();
    }

    public addNotification(id: string): void {
        const selected = this.itemMap[id];
        if (!selected) {
            return;
        }

        const { row, column } = selected;

        this.unselectAll();
        this.selectedColumn = column;
        this.selectedIndex = row;
        this.addSelection(id, SelectionFocus.FOCUS, SelectionType.NOTIFICATION);
        this.columnPillContainers[column]?.refreshCount();
        delete this.selectionStart;
        delete this.selectionEnd;
    }

    private findItemByGlobalIndex(index: number): { column: number; index: number; } | undefined {
        if (!this.columns) {
            return undefined;
        }
        let indexLeft = index;
        for (let i = 0; i < this.columns.length; i++) {
            const itemCount = this.columns[i].items.length;
            if (itemCount > indexLeft) {
                return { column: i, index: indexLeft };
            }
            indexLeft -= itemCount;
        }
        return undefined;
    }

    public selectByIndex(globalIndex: number, focus = SelectionFocus.FOCUS) {
        const found = this.findItemByGlobalIndex(globalIndex);
        if (!found) {
            return;
        }
        const { column, index } = found;
        this.selectedColumn = column;
        this.selectOne(index, focus);
    }

    private addSelection(id: string, focus = SelectionFocus.FOCUS, type = SelectionType.SELECTED) {
        this.selection.add(id);
        const card = this.stateOwners[id];
        if (card) {
            if (type !== SelectionType.NOTIFICATION) {
                card.setSelectionType(type);
            }
            const { current } = card.reference;
            if (current && focus === SelectionFocus.FOCUS && !this.currentlyOpened) {
                current.focus();
            }
        }
    }

    public hide(...ids: string[]) {
        ids.forEach(id => {
            this.deleteSelection(id);
            this.stateOwners[id]?.setSelectionType(SelectionType.HIDDEN);
        });
    }

    public unhide(...ids: string[]) {
        ids.forEach(id => this.stateOwners[id]?.setSelectionType(this.isUnread(id)
            ? SelectionType.NONE
            : SelectionType.NONE));
    }

    public isHidden(id: string): boolean {
        return this.stateOwners[id]?.selectionType === SelectionType.HIDDEN;
    }

    private deleteSelection(id: string) {
        this.selection.delete(id);
        this.stateOwners[id]?.setSelectionType(SelectionType.NONE);
    }

    public selectOne(selection: number, focus = SelectionFocus.FOCUS) {
        this.useColumn(column => {
            if (selection >= column.items.length && this.selectedColumn < (this.columns?.length ?? 0) - 1) {
                this.nextColumn();
                return;
            } if (selection < 0 && this.selectedColumn > 0) {
                this.previousColumn(Direction.END);
                return;
            }

            this.unselectAll();
            const newStart = Math.min(Math.max(0, selection), column.items.length - 1);
            this.selectedIndex = newStart;

            const item = column.items[newStart];
            if (item) {
                this.addSelection(item.id, focus);
            }
        });
    }

    private selectMany(start: number, end: number, direction: Direction) {
        this.useColumn(column => {
            this.unselectAll();
            const selectStart = Math.min(Math.max(0, start), column.items.length - 1);
            const selectEnd = Math.min(Math.max(0, end), column.items.length - 1);

            for (let i = selectStart; i <= selectEnd; i++) {
                const item = column.items[i];
                if (item) {
                    this.addSelection(item.id, SelectionFocus.NONE);
                }
            }

            this.selectionStart = selectStart;
            this.selectionEnd = selectEnd;
            this.selectedIndex = direction === Direction.START ? selectStart : selectEnd;

            const item = column.items[this.selectedIndex];
            const card = item ? this.stateOwners[item.id] : undefined;
            if (card) {
                const { current } = card.reference;
                if (current && !this.currentlyOpened) {
                    SelectionState.scrollTo(current);
                }
            }
        });
    }

    public scrollToItem(id: string) {
        const card = this.stateOwners[id];
        if (card) {
            const { current } = card.reference;
            if (current && !this.currentlyOpened) {
                SelectionState.scrollTo(current);
            }
        }
    }

    public previous(): void {
        this.selectOne(this.selectedIndex != null ? this.selectedIndex - 1 : 0);
    }

    public selectPrevious(): void {
        let start = this.selectionStart ?? (this.selectedIndex ?? 0);
        let end = this.selectionEnd ?? (this.selectedIndex ?? 0);
        let direction = Direction.START;
        if (start !== end && this.selectedIndex === end) {
            end -= 1;
            direction = Direction.END;
        } else {
            start -= 1;
        }

        this.selectMany(start, end, direction);
    }

    public next(): void {
        this.selectOne(this.selectedIndex != null ? this.selectedIndex + 1 : 0);
    }

    public selectNext(): void {
        let start = this.selectionStart ?? (this.selectedIndex ?? 0);
        let end = this.selectionEnd ?? (this.selectedIndex ?? 0);
        let direction = Direction.END;
        if (start !== end && this.selectedIndex === start) {
            start += 1;
            direction = Direction.START;
        } else {
            end += 1;
        }

        this.selectMany(start, end, direction);
    }

    public selectAllInGroup(): void {
        const groupId = this.getGroup(this.selected?.id)?.fullId;
        this.useColumn(col => {
            const start = col.items.findIndex(row => this.getGroup(row.id)?.fullId === groupId);
            const end = col.items.findIndexRight(row => this.getGroup(row.id)?.fullId === groupId);
            this.selectMany(start, end, Direction.END);
        });
    }

    public nextColumn(): void {
        if (!this.columns) {
            return;
        }

        let col = this.selectedColumn + 1;
        while (col < this.columns.length && this.columns[col].items.isEmpty) {
            col += 1;
        }

        this.selectedColumn = Math.min(col, this.columns.length - 1);
        this.selectOne(0);
    }

    public previousColumn(selectDirection = Direction.START): void {
        if (!this.columns) {
            return;
        }
        let col = this.selectedColumn - 1;
        while (col > 0 && this.columns[col].items.isEmpty) {
            col -= 1;
        }

        this.selectedColumn = Math.max(col, 0);
        this.selectOne(selectDirection === Direction.START ? 0 : this.columns[this.selectedColumn].items.length - 1);
    }

    public lastColumn(): void {
        this.selectedColumn = this.columns?.length ?? 0;
        this.previousColumn();
    }

    public firstColumn(): void {
        this.selectedColumn = -1;
        this.nextColumn();
    }

    public previousGroup(): void {
        const newIndex = this.useColumn((column): number | undefined => {
            if (!this.selectedIndex) {
                return undefined;
            }
            const item = column.items[this.selectedIndex];
            const groupIndex = column.groups.findIndex(group => group.items.some(row => row === item));
            if (groupIndex === 0) {
                return 0;
            }
            const currentItems = column.groups[groupIndex].items;
            const last = currentItems.firstElement !== item
                ? currentItems.firstElement
                : column.groups[groupIndex - 1].items.lastElement;
            return column.items.findIndex(row => row === last);
        });
        if (newIndex != null) {
            this.selectOne(newIndex);
        }
    }

    public nextGroup(): void {
        const newIndex = this.useColumn((column): number | undefined => {
            const item = column.items[this.selectedIndex ?? 0];
            const groupIndex = column.groups.findIndex(group => group.items.some(row => row === item));
            if (groupIndex === column.groups.length - 1) {
                return column.items.length - 1;
            }

            const currentItems = column.groups[groupIndex].items;
            const last = currentItems.lastElement !== item
                ? currentItems.lastElement
                : column.groups[groupIndex + 1].items.firstElement;
            return column.items.findIndex(row => row === last);
        });
        if (newIndex != null) {
            this.selectOne(newIndex);
        }
    }

    public open(): void {
        const item = this.useColumn(col => col.items[this.selectedIndex ?? 0]);
        if (item) {
            this.setOpened(item.id);
            this.routing?.historyPush(Paths.AGENT_CHAT, { chatId: item.ticketNumber ?? item.id });
        }
    }

    public get currentId(): string | undefined {
        return this.currentlyOpened;
    }

    public get currentIdOrError(): string {
        if (!this.currentlyOpened) {
            throw new Error("Current id is unknown");
        }
        return this.currentlyOpened;
    }

    public closeCurrent(): void {
        if (this.currentlyOpened) {
            this.unselect(this.currentlyOpened);
            delete this.currentlyOpened;
        }

        this.keyboardState?.popContext("chat");
        this.routing?.historyPushWithSearch(Paths.AGENT, this.routing.getSearchParams(Paths.AGENT));
    }

    public register(shortcuts: ScopedClientApi): void {
        shortcuts.on({
            shortcut: "down",
            description: "Next Chat",
            group: "Navigation",
            context: "default",
            handler: () => this.next(),
        });
        shortcuts.on({
            shortcut: "up",
            description: "Previous Chat",
            group: "Navigation",
            context: "default",
            handler: () => this.previous(),
        });
        shortcuts.on({
            shortcut: "tab",
            description: "Next Chat",
            group: "Navigation",
            context: "default",
            handler: () => this.next(),
        });
        shortcuts.on({
            shortcut: "shift+tab",
            description: "Previous Chat",
            group: "Navigation",
            context: "default",
            handler: () => this.previous(),
        });
        if (this.direction === MoveDirection.HORIZONTAL) {
            shortcuts.on({
                shortcut: "right",
                description: "Next column",
                group: "Navigation",
                context: "default",
                handler: () => this.nextColumn(),
            });
            shortcuts.on({
                shortcut: "left",
                description: "Previous column",
                group: "Navigation",
                context: "default",
                handler: () => this.previousColumn(),
            });
        }

        shortcuts.on({
            shortcut: "enter",
            description: "Opens the current chat",
            group: "Navigation",
            context: "default",
            handler: () => this.open(),
        });

        shortcuts.on({
            shortcut: "cmd+down",
            description: "Next group.",
            group: "Navigation",
            context: "default",
            handler: () => this.nextGroup(),
        });
        shortcuts.on({
            shortcut: "cmd+up",
            description: "Previous group",
            group: "Navigation",
            context: "default",
            handler: () => this.previousGroup(),
        });

        shortcuts.on({
            shortcut: "cmd+right",
            description: "Last column",
            group: "Navigation",
            context: "default",
            handler: () => this.lastColumn(),
        });
        shortcuts.on({
            shortcut: "cmd+left",
            description: "First column",
            group: "Navigation",
            context: "default",
            handler: () => this.firstColumn(),
        });

        shortcuts.on({
            shortcut: "cmd+a",
            description: "Selects all in the current group",
            group: "Selection",
            context: "default",
            handler: () => this.selectAllInGroup(),
        });

        shortcuts.on({
            shortcut: "shift+up",
            description: "Select the previous conversation",
            group: "Selection",
            context: "default",
            handler: () => this.selectPrevious(),
        });
        shortcuts.on({
            shortcut: "shift+down",
            description: "Select the next conversation",
            group: "Selection",
            context: "default",
            handler: () => this.selectNext(),
        });
        shortcuts.on({
            shortcut: "m+a",
            description: "Minimize all",
            group: "Selection",
            context: "default",
            handler: () => this.minimizeAll(),
        });

        shortcuts.on({
            shortcut: "esc",
            description: "Clear selection",
            group: "Selection",
            context: "default",
            handler: () => this.clearSelection(),
        });

        shortcuts.on({
            shortcut: "k+r",
            description: "Marks conversations as read",
            group: "Conversations",
            context: "default",
            handler: () => this.markRead(...this.getSelection().toIdList("id")),
        });

        shortcuts.on({
            shortcut: "k+c",
            description: "Marks selected conversations as unread",
            group: "Conversations",
            context: "default",
            handler: () => this.markUnread(...this.getSelection().toIdList("id")),
        });

        shortcuts.addReset(" ");
    }
}
export const SelectionStateContext = React.createContext<SelectionState>(new SelectionState());

export function SelectionStateProvider({ children }: { children: ReactElement; }): (ReactElement | null) {
    const routing = useRouting();
    const keyboardState = useKeyboardState();
    const [state] = useState(() => new SelectionState(routing, keyboardState));
    useKeyboardShortcuts(shortcuts => {
        state.register(shortcuts);
    });
    return (
        <SelectionStateContext.Provider value={state}>
            {children}
        </SelectionStateContext.Provider>
    );
}

export default function useSelectionState(): SelectionState {
    return useContext(SelectionStateContext);
}
